import { KnowledgeBaseCategory, KnowledgeBaseLevel } from '@/types/knowledgeBase'
import type { KnowledgeBaseCategoryConfigDto, KnowledgeBaseCategoryStatusDto, KnowledgeBaseSummaryViewDto } from '@/types/knowledgeBase'
import type { KnowledgeBaseShow } from '../routes'
import type { KnowledgeBaseScope } from './useKnowledgeBaseData'

/**
 * How the knowledge base page reads a summary (ORB-830): which group each entry sits in, what state its row shows, and
 * which entry the page opens on.
 *
 * ⚑ Whether emails can be created is not worked out here. The summary carries the API's answer, from the rule the email
 * refusals use, so the page and a refused email cannot disagree.
 */

export interface EntryGroup<Status extends KnowledgeBaseCategoryStatusDto = KnowledgeBaseCategoryStatusDto> {
  label: string
  entries: Status[]
}

/** The page's groups in reading order: what sending needs first, then what Webster writes from. */
const GROUPS: readonly { label: string; categories: readonly string[] }[] = [
  { label: 'Legal and sending', categories: [KnowledgeBaseCategory.CompanyAddress, KnowledgeBaseCategory.Compliance] },
  {
    label: 'About the company',
    categories: [
      KnowledgeBaseCategory.Snapshot,
      KnowledgeBaseCategory.MissionVisionValues,
      KnowledgeBaseCategory.CoreNarrativeAndPillars,
      KnowledgeBaseCategory.Offers,
      KnowledgeBaseCategory.CaseStudiesAndTestimonials,
      KnowledgeBaseCategory.Faqs,
    ],
  },
  {
    label: 'Audience',
    categories: [
      KnowledgeBaseCategory.IdealCustomerProfile,
      KnowledgeBaseCategory.PainPoints,
      KnowledgeBaseCategory.Objections,
      KnowledgeBaseCategory.Keywords,
    ],
  },
  {
    label: 'Voice and brand',
    categories: [
      KnowledgeBaseCategory.ToneOfVoice,
      KnowledgeBaseCategory.BrandGuidelines,
      KnowledgeBaseCategory.VisualNarrative,
      KnowledgeBaseCategory.DefaultMarketingLanguage,
      KnowledgeBaseCategory.Content,
    ],
  },
]

/** The summary's entries in the page's groups. An entry this build has no group for still shows, in a last group. */
export function groupEntries<Status extends KnowledgeBaseCategoryStatusDto>(statuses: readonly Status[]): EntryGroup<Status>[] {
  const byCategory = new Map(statuses.map(status => [status.Category, status]))
  const placed = new Set(GROUPS.flatMap(group => group.categories))
  const groups: EntryGroup<Status>[] = GROUPS.map(group => ({
    label: group.label,
    entries: group.categories.flatMap(category => byCategory.get(category) ?? []),
  }))
  groups.push({ label: 'More', entries: statuses.filter(status => !placed.has(status.Category)) })
  return groups.filter(group => group.entries.length > 0)
}

type EmailGaps = Pick<KnowledgeBaseSummaryViewDto, 'PostalAddressMissing' | 'EmailColorsMissing'>

/** Webster's words that nobody has reviewed. Pressing Looks right, or editing them, makes them a person's. */
export function wordsAreDraft(status: KnowledgeBaseCategoryStatusDto): boolean {
  return status.IsPopulated && status.IsAiGenerated && !status.IsUserReviewed
}

/**
 * An entry with anything of Webster's that nobody has reviewed: its words, or for Brand Guidelines its website colors,
 * which are confirmed separately. An unconfirmed import or person set is not Webster's, so it is not a draft.
 */
export function isDraft(status: KnowledgeBaseCategoryStatusDto): boolean {
  return wordsAreDraft(status) || (status.IsPopulated && status.ColorsAreWebsterDraft)
}

/** Whether an entry is one emails are waiting for: the address, or Brand Guidelines while it has no email colors. */
export function isNeededForEmail(status: KnowledgeBaseCategoryStatusDto, gaps: EmailGaps): boolean {
  return (status.Category === KnowledgeBaseCategory.CompanyAddress && gaps.PostalAddressMissing)
    || (status.Category === KnowledgeBaseCategory.BrandGuidelines && gaps.EmailColorsMissing)
}

export type EntryState = 'missing' | 'draft' | 'filled' | 'empty'

/** What an entry's row shows. Missing outranks the rest, because it is the one state that blocks sending. */
export function entryState(status: KnowledgeBaseCategoryStatusDto, gaps: EmailGaps): EntryState {
  if (isNeededForEmail(status, gaps)) return 'missing'
  if (!status.IsPopulated) return 'empty'
  return isDraft(status) ? 'draft' : 'filled'
}

/** Whether an entry belongs in the list the page is showing. */
export function matchesShow(status: KnowledgeBaseCategoryStatusDto, show: KnowledgeBaseShow): boolean {
  if (show === 'drafts') return isDraft(status)
  if (show === 'empty') return !status.IsPopulated
  return true
}

export type EntrySource = 'own' | 'workspace' | 'company-wide'

/**
 * Where a workspace's or a campaign's entry comes from, read from a summary asked for at that level. A campaign's can
 * come from its workspace's own entry; a workspace's never can.
 *
 * ⚑ Its own whenever the level has an entry of its own, empty or not — an empty override is the level saying it has
 * nothing to add here, and reading it as inherited would hide both the entry and the way to delete it.
 */
export function entrySource(status: KnowledgeBaseCategoryStatusDto): EntrySource {
  if (status.HasEntryAtThisLevel) return 'own'
  return status.EffectiveLevel === KnowledgeBaseLevel.Workspace ? 'workspace' : 'company-wide'
}

/**
 * Whether a level may hold an entry of its own in this category (ORB-1027). Every category may be set company-wide and
 * for one workspace; a campaign only where `CampaignOverridable` says so, which closes Brand Guidelines, Snapshot,
 * Mission, Vision and Values, and Compliance to campaigns. The API's validator applies the same rule.
 */
export function levelCanHold(scope: KnowledgeBaseScope, config: Pick<KnowledgeBaseCategoryConfigDto, 'CampaignOverridable'>): boolean {
  return scope !== 'campaign' || config.CampaignOverridable
}

/**
 * A Company Address's fields in reading order, the business name first because the footer prints it first (ORB-1027).
 * Each is as wide in the read view as in the form, so Edit swaps text for boxes in place.
 */
export const ADDRESS_ROWS = [
  { key: 'BusinessName', label: 'Business name', wide: true },
  { key: 'StreetAddress', label: 'Street address', wide: true },
  { key: 'StreetAddressLineTwo', label: 'Address line 2', wide: true },
  { key: 'City', label: 'City', wide: false },
  { key: 'RegionOrState', label: 'Region or state', wide: false },
  { key: 'PostalCode', label: 'Postal code', wide: false },
] as const

/** The names a row or an entry can say an entry belongs to. */
export interface LevelNames {
  companyName: string
  workspaceName: string
  /** The level shown below company-wide: the campaign's name in its section, and the workspace's elsewhere. */
  levelName: string
}

/** Whose entry a row says it is: the level's own, its workspace's, or the company's (ORB-1027). */
export function ownerOf(source: EntrySource, names: LevelNames): { name: string; own: boolean } {
  if (source === 'own') return { name: names.levelName, own: true }
  return { name: source === 'workspace' ? names.workspaceName : names.companyName, own: false }
}

/** Who may change the knowledge base, as the API decides it (ORB-817). */
export interface KnowledgeBaseRights {
  /** An admin role on the company: every company-wide entry, and the knowledge base agent. */
  companyAdmin: boolean
  /** Edit rights on the workspace: its own entries and its campaigns'. */
  workspaceEditor: boolean
}

export function canChangeLevel(scope: KnowledgeBaseScope, rights: KnowledgeBaseRights): boolean {
  return scope === 'company' ? rights.companyAdmin : rights.workspaceEditor
}

/**
 * Whether the readiness strip offers to add the email colors. They live on Brand Guidelines, which a company admin changes
 * company-wide and anyone who can edit a workspace changes for it (ORB-1027). From a campaign it opens the brand the
 * campaign uses, with the way to wherever that one changes.
 */
export function canAddEmailColors(scope: KnowledgeBaseScope, rights: KnowledgeBaseRights): boolean {
  return scope === 'company' ? rights.companyAdmin : rights.workspaceEditor || rights.companyAdmin
}

/**
 * Nothing is filled in at this level or above it, so the knowledge base has not been set up. A summary reports what a
 * level uses, so a workspace or campaign reads as empty only when the company-wide entries are empty too.
 */
export function nothingFilledIn(summary: Pick<KnowledgeBaseSummaryViewDto, 'Categories'>): boolean {
  return summary.Categories.every(status => !status.IsPopulated)
}

/** The entry the page opens on: the first one emails are waiting for, or else the first one listed. */
export function firstEntryToOpen(groups: readonly EntryGroup[], gaps: EmailGaps): string | null {
  const listed = groups.flatMap(group => group.entries)
  return listed.find(status => isNeededForEmail(status, gaps))?.Category ?? listed[0]?.Category ?? null
}
